/* eslint-disable max-lines-per-function */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './dataTable.scss';
import DataGridComponent from '../../../../Components/DataGrid/dataGrid';
import { DECLARE_PERFORMANCE_PERIOD } from '../../../../Constants/strings';
import { AuthContext } from '../../../../Contexts/AuthContext';
import EditIcon from '../../../../Assets/images/svg/dar_pencil.svg';
import DisabledEditIcon from '../../../../Assets/images/svg/dar_pencil_gray.svg';
import AddEditPerformanceCycleForm from '../../AddPerformanceCycle/addPerformanceCycle';
import { MODULE_NAMES } from '../../../../Constants/modules';

const DataTable = ({ rows, setRows }) => { // NOSONAR
  const [isMobileView, setIsMobileView] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalData, setEditModalData] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const { userProfile } = useContext(AuthContext);
  const permission = userProfile?.permissions
    ? userProfile.permissions[MODULE_NAMES.MASTER_PERFORMANCE_PERIOD] || []
    : [];

  const hasAccess = (action) => {
    return permission.includes(action);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) {
      // Check if the date is invalid
      return 'Invalid date'; // Return an error message or handle as needed
    }
    return `${date.toLocaleString('default', { month: 'short' })} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const formatLastUpdatedDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) {
      // Check if the date is invalid
      return 'Invalid date'; // Return an error message or handle as needed
    }

    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    const formattedDate = date.toLocaleString('en-US', options);

    let hours = date.getHours();
    const minutes = date.getMinutes().toString()
      .padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours || 12; // the hour '0' should be '12'

    return `${formattedDate} ${hours}:${minutes}${ampm}`;
  };

  const columns = [
    {
      field: DECLARE_PERFORMANCE_PERIOD.MASTER_TABLE_FIELD.CYCLE_NAME,
      headerName: DECLARE_PERFORMANCE_PERIOD.MASTER_TABLE_HEADER.PERFORMANCE_PERIOD,
      width: isMobileView ? 190 : undefined,
      flex: !isMobileView ? 0.2 : undefined,
      sortable: false,
      disableColumnMenu: true,
      hideCoumnMenuIcon: true,
    },
    {
      field: DECLARE_PERFORMANCE_PERIOD.MASTER_TABLE_FIELD.START_DATE,
      headerName: DECLARE_PERFORMANCE_PERIOD.MASTER_TABLE_HEADER.START_DATE,
      width: isMobileView ? 120 : undefined,
      flex: !isMobileView ? 0.12 : undefined,
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (params) => formatDate(params),
      hideCoumnMenuIcon: true,
    },
    {
      field: DECLARE_PERFORMANCE_PERIOD.MASTER_TABLE_FIELD.END_DATE,
      headerName: DECLARE_PERFORMANCE_PERIOD.MASTER_TABLE_HEADER.END_DATE,
      width: isMobileView ? 120 : undefined,
      flex: !isMobileView ? 0.12 : undefined,
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (params) => formatDate(params),
      hideCoumnMenuIcon: true,
    },
    {
      field: DECLARE_PERFORMANCE_PERIOD.MASTER_TABLE_FIELD.LAST_UPDATED_AT,
      headerName: DECLARE_PERFORMANCE_PERIOD.MASTER_TABLE_HEADER.LAST_UPDATED,
      width: isMobileView ? 142 : undefined,
      flex: !isMobileView ? 0.16 : undefined,
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (params) => formatLastUpdatedDate(params),
      hideCoumnMenuIcon: true,
    },
  ];

  const mobileColumns = [];
  const desktopColumns = [];

  if (isMobileView) {
    mobileColumns.push(...columns);
    mobileColumns.push({
      field: DECLARE_PERFORMANCE_PERIOD.MASTER_TABLE_FIELD.ACTION,
      headerName: DECLARE_PERFORMANCE_PERIOD.MASTER_TABLE_HEADER.ACTION,
      headerClassName: 'action-column',
      cellClassName: 'action-cell',
      type: 'number',
      width: isMobileView ? 100 : undefined,
      renderCell: (params) => (
        <button
          disabled={!hasAccess('update')} // Disable edit button based on permission
          id="skillMaster-edit-icon"
          className="skill-action-edit-icon"
          onClick={() => {
            setEditModalData(params?.row);
            setEditModalOpen(true);
          }} // Add onClick event
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: !hasAccess('update') ? 'not-allowed' : 'pointer',
          }}
          aria-label="Edit"
        >
          <img
            src={!hasAccess('update') ? DisabledEditIcon : EditIcon}
            alt="Edit"
          />
        </button>
      ),
      sortable: false,
      disableColumnMenu: true,
    });
  } else {
    desktopColumns.push(...columns);
    desktopColumns.push({
      field: DECLARE_PERFORMANCE_PERIOD.MASTER_TABLE_FIELD.ACTION,
      headerName: DECLARE_PERFORMANCE_PERIOD.MASTER_TABLE_HEADER.ACTION,
      headerClassName: 'action-column',
      cellClassName: 'action-cell',
      type: 'number',
      flex: 0.10,
      renderCell: (params) => (
        <button
          disabled={!hasAccess('update')} // Disable edit button based on permission
          id="skillMaster-edit-icon"
          className="skill-action-edit-icon"
          onClick={() => {
            setEditModalData(params?.row);
            setEditModalOpen(true);
          }} // Add onClick event
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: !hasAccess('update') ? 'not-allowed' : 'pointer',
          }}
          aria-label="Edit"
        >
          <img
            src={!hasAccess('update') ? DisabledEditIcon : EditIcon}
            alt="Edit"
          />
        </button>
      ),
      sortable: false,
      disableColumnMenu: true,
    });
  }

  const sortFilterColumns = [
    columns[0].field,
    columns[1].field,
    columns[2].field,
    columns[3].field,
  ];


  useEffect(() => {
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  });

  const checkMobileView = () => {
    const width = window.innerWidth;
    setIsMobileView(width < 730);
  };


  return (
    <div className="data-grid-container">
      {rows ? (
        <DataGridComponent
          dataRows={rows}
          dataColumns={isMobileView ? mobileColumns : desktopColumns}
          isFilteredRows={rows}
          sortFilterColumns={sortFilterColumns}
          pageSize={10}
          isEdit={isEdit}
        />
      ) : null}
      <AddEditPerformanceCycleForm
        modalOpen={editModalOpen}
        handleClose={() => {
          setEditModalOpen(false);
          setIsEdit(false);
        }}
        lastUpdatedBy={userProfile?.user_name}
        setRows={setRows}
        isEdit={true}
        isEditMode={setIsEdit}
        data={editModalData}
      />
    </div>
  );
};

DataTable.propTypes = {
  rows: PropTypes.array.isRequired,
  setRows: PropTypes.func.isRequired,
};

export default DataTable;
